import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { useEffect } from 'react'

import { type OnboardingPage } from '../routes'
import AddressAutoComplete from '../../components/quotation/addressAutoComplete/AddressAutoComplete'
import { useQuotation } from '../context'
import { CountryEnum, OccupancyType, UserTypeEnum } from '../../generated/graphql'
import { useOnboardingWorkflow } from '../workflow'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { OnboardingNextButton } from '../components/OnboardingNextButton'
import { OnboardingButtons } from '../components/OnboardingButtons'
import { track } from '../../utils/analytics'

import { Checkbox, FormError, Input, Select } from '@olino/design-system'
import locale from '../../utils/locale'

// This is a duplicate of the WelcomePage component in src/pages/onboarding/00-Welcome/Welcome.tsx. The goal is to show how to use the new layout in the onboarding flow.
// When the new layout is ready, we will solely use this WelcomePage and remove the one in src/pages/onboarding/00-Welcome/Welcome.tsx.

const schema = z.object({
  line1: z
    .string({ required_error: 'Ce champ est requis' })
    .trim()
    .min(1, { message: 'Ce champ est requis' }),
  line2: z.string().optional(),
  city: z
    .string()
    .trim()
    .regex(/\D/, { message: 'Ce champ est requis et ne dois pas contenir de chiffre' })
    .min(1, { message: 'Ce champ est requis' }),
  postalCode: z
    .string({ required_error: 'Ce champ est requis' })
    .trim()
    .regex(/^\d{5}$/, { message: 'Le code postal est invalide' }),
  historicalMonument: z.boolean({ required_error: 'Ce champ est requis' }),
  surfaceArea: z
    .number({ required_error: 'Ce champ est requis' })
    .gt(0, { message: 'La surface doit être supérieure à 0' }),
  procedureCollective: z.boolean({ required_error: 'Ce champ est requis' }),
  resilieNonPaiement: z.boolean({ required_error: 'Ce champ est requis' }),
  installElectriqueVerif: z.boolean({ required_error: 'Ce champ est requis' }),
  extincteursLocaux: z.boolean({ required_error: 'Ce champ est requis' }),
  nbSinistres: z.number({ required_error: 'Ce champ est requis' }),
})

type FormInputs = z.infer<typeof schema>

const Main = () => {
  const { id } = useParams()
  const { quotation, updateQuotation } = useQuotation()
  const { proceed } = useOnboardingWorkflow()

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: {
      line1: quotation.details?.mreAcheel?.addressMAcheel?.line1 ?? '',
      line2: quotation.details?.mreAcheel?.addressMAcheel?.line2 ?? '',
      city: quotation.details?.mreAcheel?.addressMAcheel?.city ?? '',
      postalCode: quotation.details?.mreAcheel?.addressMAcheel?.postalCode ?? '',
      historicalMonument: quotation.details?.mreAcheel?.historicalMonument ?? false,
      surfaceArea: quotation.details?.mreAcheel?.surfaceArea ?? 100,
      procedureCollective: quotation.details?.mreAcheel?.procedureCollective ?? false,
      resilieNonPaiement: quotation.details?.mreAcheel?.resilieNonPaiement ?? false,
      installElectriqueVerif: quotation.details?.mreAcheel?.installElectriqueVerif ?? false,
      extincteursLocaux: quotation.details?.mreAcheel?.extincteursLocaux ?? false,
      nbSinistres: quotation.details?.mreAcheel?.nbSinistres ?? 0,
    },
    resolver: zodResolver(schema),
    mode: 'onTouched',
  })

  const onSubmit = handleSubmit((data) => {
    track({
      event: 'submitted_mre_acheel_building_info_address',
      quotation_id: id,
    })

    proceed(() =>
      updateQuotation({
        details: {
          billingAddress: {
            line1: data.line1,
            line2: data.line2,
            city: data.city,
            postalCode: data.postalCode,
            country: CountryEnum.France,
          },
          contact: {
            userTypeName: UserTypeEnum.Professionnel,
          },
          mreAcheel: {
            addressMAcheel: {
              line1: data.line1,
              line2: data.line2,
              city: data.city,
              postalCode: data.postalCode,
              country: CountryEnum.France,
            },
            historicalMonument: data.historicalMonument,
            surfaceArea: data.surfaceArea,
            procedureCollective: data.procedureCollective,
            resilieNonPaiement: data.resilieNonPaiement,
            installElectriqueVerif: data.installElectriqueVerif,
            extincteursLocaux: data.extincteursLocaux,
            nbSinistres: data.nbSinistres,
          },
        },
      })
    )
  })

  const onAutoCompleteChange = (data: { line1: string; city?: string; postalCode?: string }) => {
    setValue('line1', data.line1, { shouldValidate: true })

    if (data.city) setValue('city', data.city, { shouldValidate: true })
    if (data.postalCode) setValue('postalCode', data.postalCode, { shouldValidate: true })
  }

  useEffect(() => {
    track({
      event: 'visited_mre_acheel_building_info_address',
      quotation_id: id,
    })
  }, [id])

  return (
    <>
      <OnboardingTitle.H1>Quelle est l'adresse de votre local&nbsp;?</OnboardingTitle.H1>

      <form className="w-full space-y-8" onSubmit={onSubmit}>
        <div className="space-y-3">
          <div>
            <Controller
              control={control}
              name="line1"
              render={({ field: { value, onChange } }) => (
                <AddressAutoComplete
                  onLine1Change={onChange}
                  onAddressChange={onAutoCompleteChange}
                  line1={value}
                  label="Mon local est situé"
                />
              )}
            />
          </div>

          <div>
            <Input label="Complément d'adresse" {...register('line2')} />
            <FormError error={errors.line2} />
          </div>

          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <div className="w-full sm:w-[25%]">
              <Input label="Code postal" {...register('postalCode')} />
              <FormError error={errors.postalCode} />
            </div>

            <div className="w-full sm:w-[75%]">
              <Input label="Ville" {...register('city')} />
              <FormError error={errors.city} />
            </div>
          </div>
        </div>
      </form>

      <OnboardingTitle.H1 className="mt-8">
        Quelques informations complémentaires
      </OnboardingTitle.H1>

      <form className="w-full space-y-8" onSubmit={onSubmit}>
        <div className="space-y-3">
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Controller
              name="surfaceArea"
              control={control}
              render={({ field }) => (
                <div className="w-full sm:w-[50%]">
                  <Input
                    label="Superficie de vos locaux"
                    endAdornment="m²"
                    value={field?.value?.toLocaleString(locale) || ''}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      const value = parseInt(e.target.value.replace(/\D/g, ''), 10)

                      field.onChange(Number.isNaN(value) ? 0 : value)
                    }}
                  />
                  <FormError error={errors.surfaceArea} />
                </div>
              )}
            />
          </div>

          {/*<Controller*/}
          {/*  name="historicalMonument"*/}
          {/*  control={control}*/}
          {/*  render={({ field }) => (*/}
          {/*    <div className="col-span-6">*/}
          {/*      <Checkbox*/}
          {/*        value={field.value}*/}
          {/*        label="Votre bien est-il un monument historique ?"*/}
          {/*        onChange={(v) => field.onChange(v)}*/}
          {/*        name={field.name}*/}
          {/*      />*/}
          {/*      <FormError error={errors.historicalMonument} />*/}
          {/*    </div>*/}
          {/*  )}*/}
          {/*/>*/}

          <Controller
            name="procedureCollective"
            control={control}
            render={({ field }) => (
              <div className="col-span-6">
                <Checkbox
                  value={field.value}
                  label="La société fait-elle actuellement l'objet d'une procédure collective (sauvegarde, redressement judiciaire ou liquidation judiciaire) ?"
                  onChange={(v) => field.onChange(v)}
                  name={field.name}
                />
                <FormError error={errors.procedureCollective} />
              </div>
            )}
          />

          <Controller
            name="resilieNonPaiement"
            control={control}
            render={({ field }) => (
              <div className="col-span-6">
                <Checkbox
                  value={field.value}
                  label="Au cours des 36 derniers mois, un contrat d'assurance de la société a-t-il été résilié par un assureur pour non-paiement des cotisations ?"
                  onChange={(v) => field.onChange(v)}
                  name={field.name}
                />
                <FormError error={errors.resilieNonPaiement} />
              </div>
            )}
          />

          <Controller
            name="installElectriqueVerif"
            control={control}
            render={({ field }) => (
              <div className="col-span-6">
                <Checkbox
                  value={field.value}
                  label="Les installations électriques des locaux assurés sont-elles vérifiées périodiquement par un organisme ou un professionnel habilité ?"
                  onChange={(v) => field.onChange(v)}
                  name={field.name}
                />
                <FormError error={errors.installElectriqueVerif} />
                {field.value ? (
                  <span className="text-sm">
                    Le dernier certificat de vérification électrique (Q4 ou document équivalent,
                    selon la réglementation applicable) devra être transmis au plus tard à la date
                    de prise d'effet du contrat.
                  </span>
                ) : (
                  <span className="text-sm">
                    Le certificat de vérification électrique (Q4 ou document équivalent, selon la
                    réglementation applicable) devra être transmis au plus tard 6 mois après la
                    prise d'effet du contrat.
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="extincteursLocaux"
            control={control}
            render={({ field }) => (
              <div className="col-span-6">
                <Checkbox
                  value={field.value}
                  label="Les extincteurs présents dans les locaux assurés sont-ils vérifiés périodiquement par un organisme ou un professionnel habilité ?"
                  onChange={(v) => field.onChange(v)}
                  name={field.name}
                />
                <FormError error={errors.extincteursLocaux} />
                {field.value ? (
                  <span className="text-sm">
                    Le dernier certificat de vérification des extincteurs (Q18 ou document
                    équivalent, selon la réglementation applicable) devra être transmis au plus tard
                    à la date de prise d'effet du contrat.
                  </span>
                ) : (
                  <span className="text-sm">
                    Le certificat de vérification des extincteurs (Q18 ou document équivalent, selon
                    la réglementation applicable) devra être transmis au plus tard 6 mois après la
                    prise d'effet du contrat.
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="nbSinistres"
            control={control}
            render={({ field }) => (
              <div className="w-full sm:w-[50%]">
                <Input
                  label="Nombre de sinistre dans les 36 derniers mois "
                  // endAdornment="€"
                  value={field?.value?.toLocaleString(locale) || ''}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    const value = parseInt(e.target.value.replace(/\D/g, ''), 10)

                    field.onChange(Number.isNaN(value) ? 0 : value)
                  }}
                />
                <FormError error={errors.nbSinistres} />
              </div>
            )}
          />
        </div>

        <OnboardingButtons>
          <OnboardingNextButton disabled={!isValid} />
        </OnboardingButtons>
      </form>
    </>
  )
}

const Tips = () => {
  return undefined
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
